/**
 * Company names for referral keys — one shared cache, looked up in bulk (25 Sep 2026).
 *
 * Reads the shared cache document (cache/company_names) once, for instant names (once it CAN
 * be read: a refused or failed read is tried again, see initialize). Anything
 * missing or older than 24 hours is asked of the member-only `lookupCompanyNames` callable —
 * ONE call for every key a page needs, however many components ask for names in the same
 * moment (requests are gathered for 20 ms). The server searches HubSpot 100 keys at a time
 * and writes the cache; this file never writes it.
 *
 * ⚠️ A failed lookup is never remembered, so it is retried. A key HubSpot does not know is
 * remembered for 24 hours, so it is not retried on every page load. Until 25 Sep each page
 * asked the PUBLIC basicCompanyInfo endpoint once per key, and Resource Orders kept its own
 * second cache that stored a failed lookup's KEY as the company name for 7 days.
 */
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";

const TTL_MS = 24 * 60 * 60 * 1000;
const GATHER_MS = 20;
const MAX_PER_CALL = 300;

class CompanyCache {
  constructor() {
    this.entries = new Map(); // lower-case key -> { name: string|null, checkedAtMs }
    this.inFlight = new Map(); // key -> promise of the call that will answer it
    this.gathering = null; // { keys: Set, promise }
    this.loaded = false; //    the shared cache HAS been read. A read that failed is not this.
    this.loadError = null; //  why the last read failed (its code), or null when it worked
    this.initializing = null;
    this.lastUpdated = null;
  }

  /**
   * Read the shared cache, until it has been read once.
   *
   * ⚠️ A read that FAILED is not remembered as done: the next call tries again. Until Oct 2026
   * it was. The app asks for this read as it starts, and on a fresh sign-in nobody is signed in
   * at that moment, so the read is refused; the cache was then marked "initialised" with
   * nothing in it for the rest of the page's life. No page showed it (each falls through to the
   * function and gets its names anyway), but Settings counted and listed this empty copy, and
   * its re-check button, finding no keys, said no names were held (Lee, on a preview, 7 Oct
   * 2026). "Could not read" must never be kept as "read, and empty".
   */
  async initialize() {
    if (this.loaded) return;
    if (!this.initializing) {
      this.initializing = this._loadShared().finally(() => {
        this.initializing = null;
      });
    }
    await this.initializing;
  }

  /** @returns {Promise<boolean>} whether the shared cache was read (an absent document is a read) */
  async _loadShared() {
    try {
      const snap = await getDoc(doc(db, "cache", "company_names"));
      if (snap.exists()) {
        const data = snap.data();
        this.lastUpdated = data.lastUpdated || null;
        Object.entries(data.entries || {}).forEach(([k, e]) => {
          if (e && typeof e.checkedAtMs === "number") this.entries.set(k.toLowerCase(), e);
        });
        // Older shape: names without a date — shown now, re-confirmed on the next lookup.
        Object.entries(data.companies || {}).forEach(([k, name]) => {
          const lk = k.toLowerCase();
          if (!this.entries.has(lk) && name && name !== k) this.entries.set(lk, { name, checkedAtMs: 0 });
        });
      }
      this.loaded = true;
      this.loadError = null;
      return true;
    } catch (error) {
      this.loadError = (error && (error.code || error.name)) || "error";
      console.warn("Company name cache could not be read; names will be looked up, and the read tried again:", error);
      return false;
    }
  }

  _isFresh(key, now = Date.now()) {
    const e = this.entries.get(String(key).toLowerCase());
    return !!e && now - e.checkedAtMs < TTL_MS && now >= e.checkedAtMs;
  }

  /** The name to show: the company's, or the key itself when there is none. */
  nameFor(key) {
    const e = this.entries.get(String(key).toLowerCase());
    return (e && e.name) || key;
  }

  /** @returns {Promise<Map<string, string>>} key -> name (the key itself when unknown) */
  async getCompanyNames(referralKeys) {
    await this.initialize();
    const keys = [...new Set((referralKeys || []).filter((k) => typeof k === "string" && k.trim()))];
    const need = keys.filter((k) => !this._isFresh(k));
    if (need.length) await this._ask(need);
    return new Map(keys.map((k) => [k, this.nameFor(k)]));
  }

  async getCompanyName(referralKey) {
    return (await this.getCompanyNames([referralKey])).get(referralKey) || referralKey;
  }

  // Gather keys asked for in the same moment into one call; wait on any already in flight.
  _ask(keys) {
    const waits = [];
    const fresh = [];
    keys.forEach((k) => (this.inFlight.has(k) ? waits.push(this.inFlight.get(k)) : fresh.push(k)));
    if (fresh.length) {
      if (!this.gathering) {
        const batch = { keys: new Set() };
        batch.promise = new Promise((resolve) => setTimeout(resolve, GATHER_MS)).then(() => {
          this.gathering = null;
          return this._lookup([...batch.keys]);
        });
        this.gathering = batch;
      }
      const batch = this.gathering;
      fresh.forEach((k) => {
        batch.keys.add(k);
        this.inFlight.set(k, batch.promise);
      });
      waits.push(batch.promise);
    }
    return Promise.all(waits);
  }

  async _lookup(keys, { refresh = false } = {}) {
    const outcome = { names: 0, unknown: 0, failed: 0, unconfirmed: 0 };
    const call = httpsCallable(functions, "lookupCompanyNames", { timeout: 60000 });
    for (let i = 0; i < keys.length; i += MAX_PER_CALL) {
      const chunk = keys.slice(i, i + MAX_PER_CALL);
      try {
        const { data } = await call({ keys: chunk, refresh });
        const now = Date.now();
        Object.entries(data.names || {}).forEach(([k, name]) => this.entries.set(k.toLowerCase(), { name, checkedAtMs: now }));
        (data.unknown || []).forEach((k) => this.entries.set(String(k).toLowerCase(), { name: null, checkedAtMs: now }));
        outcome.names += Object.keys(data.names || {}).length;
        outcome.unknown += (data.unknown || []).length;
        outcome.failed += (data.failed || []).length; // not remembered: asked again next time
        // On a refresh, a name the function HOLDS and HubSpot did not confirm is kept as it
        // was, its check time too (see lookupCompanyNames). Nothing here is touched for it.
        // An older function sends no such list: then there are none.
        outcome.unconfirmed += (data.unconfirmed || []).length;
      } catch (error) {
        console.warn("Company name lookup failed; keys will be shown and retried:", error);
        outcome.failed += chunk.length;
      } finally {
        chunk.forEach((k) => this.inFlight.delete(k));
      }
    }
    return outcome;
  }

  /**
   * Settings → re-check names against HubSpot now, ignoring the 24-hour cache.
   *
   * With no list: every key the shared cache already holds, which is every key some page has
   * asked for. The one document that holds them is read again AT THE PRESS (this browser's
   * copy may be hours old, or may never have been read); no collection is read to find keys.
   * ⚠️ Until Oct 2026 Settings gathered the keys by reading three collections named after the
   * TOOLS' ids (bannerAds, promoterVisits, redirects). Nothing has ever written those, so the
   * button re-checked no names at all, and said "0 companies loaded", for ten months.
   * ⚠️ And in its first form this took "the keys this browser holds" for "the keys the cache
   * holds": after a refused first read it found none and said none were held. If the read at
   * the press fails, the answer is `couldNotRead` and NOTHING is re-checked: the keys this
   * browser happens to know are not "everything", and re-checking some while saying all would
   * be the same fault again.
   *
   * "Not known to HubSpot" and "could not be checked" are different answers and are kept
   * apart: the first is remembered for a day, the second is asked again. A third,
   * "unconfirmed", is a name the cache holds that HubSpot's answer did not include: the
   * function keeps it (a re-check of everything can never remove a name), and so does this.
   * @returns {Promise<{asked: number, names: number, unknown: number, failed: number, unconfirmed: number, couldNotRead?: true, readError?: string}>}
   */
  async rebuildCache(referralKeys) {
    if (referralKeys == null) {
      if (this.initializing) await this.initializing; // a read already on its way: let it land first
      if (!(await this._loadShared())) {
        return { asked: 0, names: 0, unknown: 0, failed: 0, unconfirmed: 0, couldNotRead: true, readError: this.loadError };
      }
    } else {
      await this.initialize();
    }
    const keys = [...new Set(referralKeys == null ? [...this.entries.keys()] : referralKeys)];
    const r = await this._lookup(keys, { refresh: true });
    return { asked: keys.length, names: r.names, unknown: r.unknown, failed: r.failed, unconfirmed: r.unconfirmed };
  }

  /** Not a referral key at all — never worth looking up or showing as a company. */
  isValidReferralKey(referralKey) {
    if (!referralKey) return false;
    const normalized = referralKey.trim().toLowerCase();
    return !["unknown", "undefined", "null", "none", "n/a", "test"].includes(normalized);
  }

  clear() {
    this.entries.clear();
    this.inFlight.clear();
    this.gathering = null;
    this.loaded = false;
    this.loadError = null;
    this.lastUpdated = null;
  }

  /**
   * What THIS BROWSER holds. ⚠️ Until `loaded` is true that is not what the shared cache holds:
   * `cacheSize` is then a count of whatever pages have looked up since the page opened, or 0.
   * Whoever shows these must say "not read" and not a number (Settings does).
   */
  getStats() {
    return {
      cacheSize: [...this.entries.values()].filter((e) => e.name).length,
      unknownKeys: [...this.entries.values()].filter((e) => !e.name).length,
      pendingRequests: this.inFlight.size,
      lastUpdated: this.lastUpdated,
      loaded: this.loaded,
      loadError: this.loadError,
    };
  }

  /** @returns {Map<string, string>} key -> name, for the Settings viewer */
  getAllEntries() {
    return new Map([...this.entries].filter(([, e]) => e.name).map(([k, e]) => [k, e.name]));
  }
}

export const companyCache = new CompanyCache();

/**
 * What Settings' panel says about the names held. Pure.
 *
 * ⚠️ Until the shared cache has been READ, this browser's count is not the cache's: it is 0, or
 * whatever pages happen to have looked up. So the count is "—" until then (never 0 for "not
 * counted"), and a read that failed says so; "Active" beside an empty copy was false comfort.
 * @param stats  companyCache.getStats()
 * @returns {{ count: string, updated: string|null, status: string, note: string|null }}
 */
export function cachePanel(stats) {
  const s = stats || {};
  if (!s.loaded) {
    return s.loadError
      ? { count: "—", updated: null, status: "⚠️ Could not be read", note: "The names held could not be read just now, so this panel cannot say how many there are. Pages still show names. Try again in a moment, or reload the page." }
      : { count: "—", updated: null, status: "⏳ Loading", note: null };
  }
  return { count: String(s.cacheSize || 0), updated: s.lastUpdated || null, status: "✅ Read", note: null };
}

/**
 * What to tell the person who pressed "re-check": which kind of message, and its words.
 * Pure, so it is tested without a page.
 */
export function rebuildMessage(result) {
  const { asked = 0, names = 0, unknown = 0, failed = 0, unconfirmed = 0 } = result || {};
  const n = (count, one, many) => `${count} ${count === 1 ? one : many}`;
  // "Could not read what is held" is not "nothing is held". The first is a failure to do what
  // was asked, so it is a warning and says nothing was changed; the second is information.
  if (result && result.couldNotRead) {
    return { kind: "warning", text: "The company names held could not be read, so nothing was re-checked and nothing was changed. Try again; if it keeps happening, reload the page." };
  }
  if (!asked) {
    return { kind: "info", text: "No company names are held yet, so there was nothing to re-check. A name is looked up when a page first shows a promoter." };
  }
  const parts = [`${n(names, "name", "names")} confirmed`];
  if (unconfirmed) parts.push(`${n(unconfirmed, "name was", "names were")} not confirmed by HubSpot and kept as ${unconfirmed === 1 ? "it was" : "they were"}`);
  if (unknown) parts.push(`${n(unknown, "key is", "keys are")} not known to HubSpot`);
  if (failed) parts.push(`${n(failed, "key", "keys")} could not be checked (try again)`);
  // A name HubSpot did not confirm is worth a second look, so it is a warning too. If it is
  // every name, something is wrong with the check, not with the names.
  const kind = failed || unconfirmed ? "warning" : "success";
  return { kind, text: `Re-checked ${n(asked, "key", "keys")} with HubSpot: ${parts.join(", ")}.` };
}
